import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import { OFFERS_CATEGORY_ID } from '../../utils/offers';

export const processTransaction = createAsyncThunk(
  'pos/processTransaction',
  async (transactionData, { getState, rejectWithValue }) => {
    try {
      const { auth, pos, session } = getState();
      const activeOrder = pos.orders.find(o => o.id === pos.activeOrderId);

      if (!session.current || session.current.status !== 'open') {
        throw new Error('No open session. Please open a session first.');
      }

      let customerId = null;
      if (activeOrder?.mode === 'delivery' && activeOrder.delivery.phone && activeOrder.delivery.name) {
        try {
          const customerRes = await apiService.upsertCustomer({
            name: activeOrder.delivery.name,
            phone: activeOrder.delivery.phone,
            address: activeOrder.delivery.address || '',
          });
          customerId = customerRes.data?.id || null;
        } catch (customerError) {
          console.error('Error saving customer:', customerError);
        }
      }

      const payments = [];
      if (transactionData.payment_method === 'cash') {
        payments.push({ amount: transactionData.total_amount, method: 'cash', transaction_ref: null });
      } else if (transactionData.payment_method === 'card') {
        payments.push({ amount: transactionData.total_amount, method: 'card', transaction_ref: transactionData.card_transaction_no });
      } else if (transactionData.payment_method === 'instapay') {
        payments.push({ amount: transactionData.total_amount, method: 'instapay', transaction_ref: transactionData.instapay_transaction_no });
      } else if (transactionData.payment_method === 'wallet') {
        payments.push({ amount: transactionData.total_amount, method: 'wallet', transaction_ref: transactionData.wallet_transaction_no });
      } else if (transactionData.payment_method === 'mixed') {
        if (transactionData.cash_amount && transactionData.cash_amount > 0) {
          payments.push({ amount: transactionData.cash_amount, method: 'cash', transaction_ref: null });
        }
        if (transactionData.card_amount && transactionData.card_amount > 0) {
          payments.push({ amount: transactionData.card_amount, method: 'card', transaction_ref: transactionData.card_transaction_no });
        }
        if (transactionData.instapay_amount && transactionData.instapay_amount > 0) {
          payments.push({ amount: transactionData.instapay_amount, method: 'instapay', transaction_ref: transactionData.instapay_transaction_no });
        }
        if (transactionData.wallet_amount && transactionData.wallet_amount > 0) {
          payments.push({ amount: transactionData.wallet_amount, method: 'wallet', transaction_ref: transactionData.wallet_transaction_no });
        }
      }

      // Use delivery_region to determine if it's a delivery order, not customer_id
      const isDeliveryOrder = transactionData.delivery_region && transactionData.delivery_region.trim() !== '';
      const deliveryPersonId = isDeliveryOrder ? (activeOrder?.delivery?.deliveryPersonId || null) : null;

      const orderData = {
        customer_id: customerId,
        delivery_region: transactionData.delivery_region || null,
        delivery_fee_amount: transactionData.delivery_fee_amount ?? null,
        delivery_person_id: deliveryPersonId,
        payment_method: transactionData.payment_method,
        total_amount: transactionData.total_amount,
        discount_percentage: transactionData.discount_percentage ?? 0,
        discount_approved_by: transactionData.discount_approved_by || undefined,
        discount_notes: transactionData.discount_notes || undefined,
        tax_percentage: transactionData.tax_percentage ?? 0,
        items: transactionData.items.map(item => {
          if (item.type === 'offer' || item.offer_id) {
            return {
              offer_id: item.offer_id || item.id,
              product_name: item.item_name || item.name,
              quantity: item.quantity,
              unit_price: item.unit_price,
            };
          }
          return {
            product_id: item.item_id || item.product_id,
            product_name: item.item_name || item.name,
            quantity: item.quantity,
            unit_price: item.unit_price,
          };
        }),
        payments,
      };

      const res = await apiService.createOrder(orderData);
      return res.data || res;
    } catch (err) {
      const body = err.response?.data || err.body;
      if (body && typeof body === 'object') {
        return rejectWithValue(body);
      }
      return rejectWithValue({ error: err.message || 'Failed to process transaction' });
    }
  }
);

const createEmptyOrder = (id) => ({
  id,
  mode: 'in_store',
  cart: [],
  paymentMethod: 'cash',
  delivery: { phone: '', name: '', address: '', region: '', deliveryPersonId: null },
  onHold: false,
  selectedCategory: null,
  discountPercentage: 0,
});

const posSlice = createSlice({
  name: 'pos',
  initialState: {
    orders: [],
    activeOrderId: null,
    loading: false,
    error: null,
    lastTransaction: null,
    showReceipt: false,
  },
  reducers: {
    createNewOrder: (state, action) => {
      const id = action.payload || `order-${Date.now()}`;
      state.orders.push(createEmptyOrder(id));
      state.activeOrderId = id;
    },
    switchOrder: (state, action) => {
      state.activeOrderId = action.payload;
    },
    closeOrder: (state, action) => {
      const id = action.payload;
      state.orders = state.orders.filter(o => o.id !== id);
      if (state.activeOrderId === id) {
        state.activeOrderId = state.orders.length ? state.orders[0].id : null;
      }
    },
    setOrderMode: (state, action) => {
      const mode = action.payload;
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.mode = mode;
    },
    setDeliveryPhone: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.delivery.phone = action.payload || '';
    },
    setDeliveryAddress: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.delivery.address = action.payload || '';
    },
    setDeliveryName: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.delivery.name = action.payload || '';
    },
    setDeliveryRegion: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.delivery.region = action.payload || '';
    },
    setDeliveryPersonId: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.delivery.deliveryPersonId = action.payload || null;
    },
    setOrderOnHold: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.onHold = !!action.payload;
    },
    addToCart: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (!order) return;
      const item = action.payload;
      // Use quantity from payload if provided, otherwise default to 0.1 for weight or 1 for piece
      const quantityToAdd = item.quantity !== undefined ? item.quantity : (item.unit_type === 'weight' ? 0.1 : 1);
      const existingItem = order.cart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        existingItem.quantity += quantityToAdd;
        existingItem.total = existingItem.quantity * existingItem.price;
      } else {
        order.cart.push({ ...item, quantity: quantityToAdd, total: item.price * quantityToAdd });
      }
    },
    removeFromCart: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (!order) return;
      const itemId = action.payload;
      order.cart = order.cart.filter(item => item.id !== itemId);
    },
    updateCartItemQuantity: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (!order) return;
      const { itemId, quantity } = action.payload;
      const item = order.cart.find(cartItem => cartItem.id === itemId);
      if (item) {
        if (quantity <= 0) {
          order.cart = order.cart.filter(cartItem => cartItem.id !== itemId);
        } else {
          item.quantity = quantity;
          item.total = quantity * item.price;
        }
      }
    },
    clearCart: (state) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) {
        order.cart = [];
        state.lastTransaction = null;
      }
    },
    clearOrderCart: (state, action) => {
      const orderId = action.payload;
      const order = state.orders.find(o => o.id === orderId);
      if (order) {
        order.cart = [];
        order.paymentMethod = 'cash';
        order.delivery = { phone: '', name: '', address: '', region: '', deliveryPersonId: null };
        order.onHold = false;
        order.selectedCategory = null;
        order.discountPercentage = 0;
      }
    },
    setSelectedCategory: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) {
        order.selectedCategory = action.payload;
      }
    },
    setPaymentMethod: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.paymentMethod = action.payload;
    },
    setOrderDiscountPercentage: (state, action) => {
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (order) order.discountPercentage = Math.min(100, Math.max(0, parseFloat(action.payload) || 0));
    },
    clearError: (state) => {
      state.error = null;
    },
    closeReceipt: (state) => {
      state.showReceipt = false;
      state.lastTransaction = null;
    },
    /** Sync cart with latest product catalog: drop deleted products, then refresh prices */
    syncCartPrices: (state, action) => {
      const items = action.payload || [];
      if (items.length === 0) return;

      const validIds = new Set(items.map((p) => p.id));
      const priceMap = Object.fromEntries(items.map((p) => [p.id, parseFloat(p.price ?? p.selling_price ?? 0) || 0]));

      for (const order of state.orders) {
        if (!order.cart?.length) continue;
        order.cart = order.cart.filter((cartItem) => validIds.has(cartItem.id));
        for (const cartItem of order.cart) {
          const newPrice = priceMap[cartItem.id];
          if (newPrice !== undefined) {
            cartItem.price = newPrice;
            cartItem.total = cartItem.quantity * newPrice;
          }
        }
      }
    },
    /** Auto-adjust cart when checkout fails with insufficient_stock: reduce qty or remove item */
    adjustCartForInsufficientStock: (state, action) => {
      const { product_id, offer_id, available } = action.payload;
      const order = state.orders.find(o => o.id === state.activeOrderId);
      if (!order?.cart) return;
      const avail = parseFloat(available) || 0;

      if (offer_id != null) {
        const idx = order.cart.findIndex(c => c.type === 'offer' && c.id === offer_id);
        if (idx === -1) return;
        const item = order.cart[idx];
        if (avail <= 0) {
          order.cart.splice(idx, 1);
        } else {
          item.quantity = avail;
          item.total = avail * (item.price || 0);
        }
        return;
      }

      const idx = order.cart.findIndex(c => c.id === product_id);
      if (idx === -1) return;
      const item = order.cart[idx];
      if (avail <= 0) {
        order.cart.splice(idx, 1);
      } else {
        item.quantity = avail;
        item.total = avail * (item.price || 0);
      }
    },
    syncOfferCartPrices: (state, action) => {
      const offers = action.payload || [];
      if (offers.length === 0) return;

      const validIds = new Set(offers.map((o) => o.id));
      const priceMap = Object.fromEntries(
        offers.map((o) => [o.id, parseFloat(o.selling_price ?? o.price) || 0])
      );

      for (const order of state.orders) {
        if (!order.cart?.length) continue;
        order.cart = order.cart.filter(
          (cartItem) => cartItem.type !== 'offer' || validIds.has(cartItem.id)
        );
        for (const cartItem of order.cart) {
          if (cartItem.type !== 'offer') continue;
          const newPrice = priceMap[cartItem.id];
          if (newPrice !== undefined) {
            cartItem.price = newPrice;
            cartItem.selling_price = newPrice;
            cartItem.total = cartItem.quantity * newPrice;
          }
        }
      }
    },
    /** Clear selected category if it was deleted (e.g. after categories_updated) */
    syncSelectedCategoryWithCategories: (state, action) => {
      const categories = action.payload || [];
      const categoryIds = new Set(categories.map((c) => c.id));
      for (const order of state.orders) {
        if (
          order.selectedCategory &&
          order.selectedCategory !== OFFERS_CATEGORY_ID &&
          !categoryIds.has(order.selectedCategory)
        ) {
          order.selectedCategory = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.lastTransaction = action.payload;
        state.showReceipt = true;
      })
      .addCase(processTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  createNewOrder,
  switchOrder,
  closeOrder,
  setOrderMode,
  setOrderOnHold,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  clearOrderCart,
  setSelectedCategory,
    setPaymentMethod,
    setOrderDiscountPercentage,
    clearError,
    closeReceipt,
    syncCartPrices,
    syncOfferCartPrices,
    syncSelectedCategoryWithCategories,
    adjustCartForInsufficientStock,
  setDeliveryName,
  setDeliveryPhone,
  setDeliveryAddress,
  setDeliveryRegion,
  setDeliveryPersonId,
} = posSlice.actions;

export default posSlice.reducer;
